import { subscribeToAIMessageInSession } from '../../service/chatService';
import { getInteractionsBySession } from '../../service/interactionService';
import { Typography, IconButton, Slider, Box } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { LineChart } from '@mui/x-charts/LineChart';
import { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { SessionContext } from '../../context/SessionContext';
import InteractionDetailsModal from './InteractionDetailsModal';
import '../../css/codeIssueGrid.scss'
import '../../css/passrate.scss'

const InteractionList = () => { 
  const { currentUser } = useContext(AuthContext);
  const { session } = useContext(SessionContext);
  
  const [questionCategories, setQuestionCategories] = useState({
    Avg_question_per_student: 0,
    Task_clarification: 0,
    Syntax_error_fix: 0,
    Implementation_challenge: 0,
    Failing_test_case_fix: 0,
    Post_completion_code_refinement: 0,
  });
  const [messages, setMessages] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [open, setOpen] = useState(false);
  const [openCategories, setOpenCategories] = useState({});
  const [chartView, setChartView] = useState('questions');
  
  // Playback controls
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [maxTime, setMaxTime] = useState(28);

  // Modal state for interaction details
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInteractions, setSelectedInteractions] = useState([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState({ start: 0, end: 0 });

  const [fullDataset, setFullDataset] = useState({
    questions: [
      { minute: 0, taskClarification: 0, syntaxError: 0, implementationChallenge: 0, failingTest: 0, codeRefinement: 0 }
    ],
    responses: [
      { minute: 0, copyPaste: 0, autoComplete: 0, typeOut: 0, applyIdeas: 0, ignore: 0 }
    ],
    intent: [
      { minute: 0, learning: 0, debugging: 0, exploring: 0, validation: 0, completion: 0 }
    ],
  });

  // Store time bucket mapping for interactions
  const [interactionsByTimeBucket, setInteractionsByTimeBucket] = useState({});

  // Fetch interactions when component mounts or session changes
  useEffect(() => {
    const fetchInteractions = async () => {
      if (session?.id) {
        const data = await getInteractionsBySession(session.id);
        console.log('Fetched interactions from DB:', data);
        setInteractions(data);
      }
    };
    
    fetchInteractions();
  }, [session?.id]);

  useEffect(() => {
    if (!session?.id) return;
    
    const pollInterval = setInterval(async () => {
      console.log('🔄 Polling for new interactions...');
      const data = await getInteractionsBySession(session.id);
      
      if (data.length !== interactions.length) {
        console.log('✅ New interactions found:', data.length - interactions.length);
        setInteractions(data);
      }
    }, 5000);
    
    return () => clearInterval(pollInterval);
  }, [session?.id, interactions.length]);

  // Process interactions into chart data when interactions change
  useEffect(() => {
    if (interactions.length > 0) {
      processInteractionsForCharts(interactions);
    }
  }, [interactions]);

  // Function to process interactions into time-series data
  const processInteractionsForCharts = (interactionsData) => {
    console.log('Starting to process interactions for charts...');
    
    // Sort by timestamp
    const sorted = [...interactionsData].sort((a, b) => 
      new Date(a.created_time) - new Date(b.created_time)
    );
    
    if (sorted.length === 0) {
      console.log('No interactions to process');
      return;
    }

    // Get session start time and end time
    const startTime = new Date(sorted[0].created_time);
    const endTime = new Date(sorted[sorted.length - 1].created_time);
    const durationMinutes = Math.ceil((endTime - startTime) / 1000 / 60);
    
    // Set max time for playback (minimum 5 minutes, round up to nearest 5)
    const calculatedMaxTime = Math.max(5, Math.ceil(durationMinutes / 5) * 5);
    setMaxTime(calculatedMaxTime);
    
    console.log('Session duration:', durationMinutes, 'minutes. Max time set to:', calculatedMaxTime);
    
    // Create time buckets (every 3 minutes)
    const bucketSize = 3; // minutes
    const numBuckets = Math.ceil(calculatedMaxTime / bucketSize);
    
    // Initialize data structures for each view
    const questionsBuckets = Array.from({ length: numBuckets + 1 }, (_, i) => ({
      minute: i * bucketSize,
      taskClarification: 0,
      syntaxError: 0,
      implementationChallenge: 0,
      failingTest: 0,
      codeRefinement: 0
    }));
    
    const responsesBuckets = Array.from({ length: numBuckets + 1 }, (_, i) => ({
      minute: i * bucketSize,
      copyPaste: 0,
      autoComplete: 0,
      typeOut: 0,
      applyIdeas: 0,
      ignore: 0
    }));
    
    const intentBuckets = Array.from({ length: numBuckets + 1 }, (_, i) => ({
      minute: i * bucketSize,
      learning: 0,
      debugging: 0,
      exploring: 0,
      validation: 0,
      completion: 0
    }));

    // Map to store interactions by time bucket
    const bucketMap = {};
    
    // Process each interaction
    sorted.forEach(interaction => {
      const interactionTime = new Date(interaction.created_time);
      const minutesFromStart = (interactionTime - startTime) / 1000 / 60;
      const bucketIndex = Math.floor(minutesFromStart / bucketSize);
      const bucketMinute = bucketIndex * bucketSize;
      
      // Store interaction in the bucket map
      if (!bucketMap[bucketMinute]) {
        bucketMap[bucketMinute] = [];
      }
      bucketMap[bucketMinute].push(interaction);
      
      // Map question types to chart fields - only increment current bucket
      const questionType = interaction.question_type;
      if (questionType) {
        // Process questions view - discrete counts per bucket
        if (questionType === 'Task Clarification') {
          questionsBuckets[bucketIndex].taskClarification++;
        } else if (questionType === 'Syntax Error') {
          questionsBuckets[bucketIndex].syntaxError++;
        } else if (questionType === 'Implementation Challenge') {
          questionsBuckets[bucketIndex].implementationChallenge++;
        } else if (questionType === 'Failing Test Case') {
          questionsBuckets[bucketIndex].failingTest++;
        } else if (questionType === 'Code Refinement' || questionType === 'Post Completion Code Refinement') {
          questionsBuckets[bucketIndex].codeRefinement++;
        }
      }
      
      // Process intent view using structured categories from DB - only increment current bucket
      const studentIntent = interaction.student_intent || '';
      
      // Direct match since the backend now returns structured categories
      if (studentIntent === 'Learning') {
        intentBuckets[bucketIndex].learning++;
      } else if (studentIntent === 'Debugging') {
        intentBuckets[bucketIndex].debugging++;
      } else if (studentIntent === 'Exploring') {
        intentBuckets[bucketIndex].exploring++;
      } else if (studentIntent === 'Validation') {
        intentBuckets[bucketIndex].validation++;
      } else if (studentIntent === 'Completion') {
        intentBuckets[bucketIndex].completion++;
      }
    });
    
    console.log('Processed questions buckets:', questionsBuckets);
    console.log('Processed intent buckets:', intentBuckets);
    console.log('Interactions by time bucket:', bucketMap);
    
    // Store the bucket map for modal access
    setInteractionsByTimeBucket(bucketMap);
    
    // Update the dataset with processed real data
    setFullDataset({
      questions: questionsBuckets,
      responses: responsesBuckets,
      intent: intentBuckets
    });
  };
  
  // Subscribe to real-time messages
  useEffect(() => {
    const unsubscribe = subscribeToAIMessageInSession(session.id, (newMessages) => {
      console.log('Raw messages data (real-time):', newMessages);
      setMessages(newMessages);
      // Process messages immediately if session type is correct
      if (session.type === "Vizmental") {
        processMessages(newMessages);
      }
    });
    return () => {
      unsubscribe();
    }
  }, [session]);
  
  const processMessages = (messages) => {
    let questionCounts = {
      Avg_question_per_student: 0,
      Task_clarification: 0,
      Syntax_error_fix: 0,
      Implementation_challenge: 0,
      Failing_test_case_fix: 0,
      Post_completion_code_refinement: 0,
    };
    let totalQuestions = 0;

    (messages || []).forEach((message) => {
      if (message.recipient_id === -4) {
        totalQuestions++;
        if (message.question_type && questionCounts.hasOwnProperty(message.question_type)) {
          questionCounts[message.question_type]++;
        }
      }
    });
    
    if (session.stu_num && session.stu_num > 0) {
      questionCounts['Avg_question_per_student'] = (totalQuestions / session.stu_num).toFixed(1);
    } else {
      questionCounts['Avg_question_per_student'] = totalQuestions.toFixed(1);
    }
    
    setQuestionCategories(questionCounts);
  };

  // Filter dataset based on current time
  const getFilteredDataset = () => {
    const dataset = fullDataset[chartView];
    return dataset.filter(point => point.minute <= currentTime);
  };

  // Handle chart data point click - uses MUI X Charts onMarkClick
  const handleChartClick = (event, params) => {
    console.log('Chart clicked:', event, params);
    
    if (!params || !params.seriesId) {
      console.log('No params or seriesId');
      return;
    }
    
    const seriesId = params.seriesId;
    const dataIndex = params.dataIndex;
    
    console.log('Series ID:', seriesId, 'Data Index:', dataIndex);
    
    const clickedPoint = fullDataset[chartView][dataIndex];
    
    if (!clickedPoint) {
      console.log('No clicked point found');
      return;
    }
    
    const minute = clickedPoint.minute;
    const interactionsForBucket = interactionsByTimeBucket[minute] || [];
    
    console.log('Getting interactions for minute:', minute, 'Found:', interactionsForBucket.length);
    
    if (interactionsForBucket.length > 0) {
      setSelectedInteractions(interactionsForBucket);
      setSelectedTimeRange({ start: minute, end: minute + 3 });
      setModalOpen(true);
    } else {
      console.log('No interactions found for this time bucket');
    }
  };

  // Playback effect
  useEffect(() => {
    if (isPlaying && currentTime < maxTime) {
      const interval = setInterval(() => {
        setCurrentTime(prev => {
          const next = prev + (1/60);
          if (next >= maxTime) {
            setIsPlaying(false);
            return maxTime;
          }
          return next;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isPlaying, currentTime, maxTime]);

  const handlePlayPause = () => {
    if (currentTime >= maxTime) {
      setCurrentTime(0);
    }
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setCurrentTime(0);
    setIsPlaying(false);
  };

  const handleSliderChange = (event, newValue) => {
    setCurrentTime(newValue);
  };

  const formatTime = (minutes) => {
    const mins = Math.floor(minutes);
    const secs = Math.floor((minutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedInteractions([]);
  };

  const renderChart = () => {
    const filteredData = getFilteredDataset();

    if (chartView === 'questions') {
      return (
        <LineChart
          height={300}
          dataset={filteredData}
          xAxis={[{
            dataKey: 'minute',
            label: 'Time (minutes)',
            scaleType: 'linear',
          }]}
          yAxis={[{
            label: 'Count',
            scaleType: 'linear',
          }]}
          series={[
            {
              id: 'taskClarification',
              dataKey: 'taskClarification',
              label: 'Task Clarification',
              color: '#3b82f6',
              showMark: true,
            },
            {
              id: 'syntaxError',
              dataKey: 'syntaxError',
              label: 'Syntax Error',
              color: '#ef4444',
              showMark: true,
            },
            {
              id: 'implementationChallenge',
              dataKey: 'implementationChallenge',
              label: 'Implementation Challenge',
              color: '#f59e0b',
              showMark: true,
            },
            {
              id: 'failingTest',
              dataKey: 'failingTest',
              label: 'Failing Test',
              color: '#8b5cf6',
              showMark: true,
            },
            {
              id: 'codeRefinement',
              dataKey: 'codeRefinement',
              label: 'Code Refinement',
              color: '#10b981',
              showMark: true,
            },
          ]}
          slotProps={{ legend: { hidden: true } }}
          margin={{ left: 60, right: 60, top: 20, bottom: 40 }}
          onMarkClick={handleChartClick}
        />
      );
    } else if (chartView === 'responses') {
      return (
        <LineChart
          height={300}
          dataset={filteredData}
          xAxis={[{
            dataKey: 'minute',
            label: 'Time (minutes)',
            scaleType: 'linear',
          }]}
          yAxis={[{
            label: 'Count',
            scaleType: 'linear',
          }]}
          series={[
            {
              id: 'copyPaste',
              dataKey: 'copyPaste',
              label: 'Copy & Paste',
              color: '#3b82f6',
              showMark: true,
            },
            {
              id: 'autoComplete',
              dataKey: 'autoComplete',
              label: 'Auto Complete',
              color: '#ef4444',
              showMark: true,
            },
            {
              id: 'typeOut',
              dataKey: 'typeOut',
              label: 'Type Out',
              color: '#f59e0b',
              showMark: true,
            },
            {
              id: 'applyIdeas',
              dataKey: 'applyIdeas',
              label: 'Apply Ideas',
              color: '#8b5cf6',
              showMark: true,
            },
            {
              id: 'ignore',
              dataKey: 'ignore',
              label: 'Ignore',
              color: '#6b7280',
              showMark: true,
            },
          ]}
          slotProps={{ legend: { hidden: true } }}
          margin={{ left: 60, right: 60, top: 20, bottom: 40 }}
          onMarkClick={handleChartClick}
        />
      );
    } else {
      return (
        <LineChart
          height={300}
          dataset={filteredData}
          xAxis={[{
            dataKey: 'minute',
            label: 'Time (minutes)',
            scaleType: 'linear',
          }]}
          yAxis={[{
            label: 'Count',
            scaleType: 'linear',
          }]}
          series={[
            {
              id: 'learning',
              dataKey: 'learning',
              label: 'Learning',
              color: '#3b82f6',
              showMark: true,
            },
            {
              id: 'debugging',
              dataKey: 'debugging',
              label: 'Debugging',
              color: '#ef4444',
              showMark: true,
            },
            {
              id: 'exploring',
              dataKey: 'exploring',
              label: 'Exploring',
              color: '#f59e0b',
              showMark: true,
            },
            {
              id: 'validation',
              dataKey: 'validation',
              label: 'Validation',
              color: '#8b5cf6',
              showMark: true,
            },
            {
              id: 'completion',
              dataKey: 'completion',
              label: 'Completion',
              color: '#10b981',
              showMark: true,
            },
          ]}
          slotProps={{ legend: { hidden: true } }}
          margin={{ left: 60, right: 60, top: 20, bottom: 40 }}
          onMarkClick={handleChartClick}
        />
      );
    }
  };

  const renderLegend = () => {
    if (chartView === 'questions') {
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#3b82f6' }}></div>
            <Typography variant="body2">Task Clarification</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#ef4444' }}></div>
            <Typography variant="body2">Syntax Error</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#f59e0b' }}></div>
            <Typography variant="body2">Implementation Challenge</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#8b5cf6' }}></div>
            <Typography variant="body2">Failing Test</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#10b981' }}></div>
            <Typography variant="body2">Code Refinement</Typography>
          </div>
        </>
      );
    } else if (chartView === 'responses') {
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#3b82f6' }}></div>
            <Typography variant="body2">Copy & Paste</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#ef4444' }}></div>
            <Typography variant="body2">Auto Complete</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#f59e0b' }}></div>
            <Typography variant="body2">Type Out</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#8b5cf6' }}></div>
            <Typography variant="body2">Apply Ideas</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#6b7280' }}></div>
            <Typography variant="body2">Ignore</Typography>
          </div>
        </>
      );
    } else {
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#3b82f6' }}></div>
            <Typography variant="body2">Learning</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#ef4444' }}></div>
            <Typography variant="body2">Debugging</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#f59e0b' }}></div>
            <Typography variant="body2">Exploring</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#8b5cf6' }}></div>
            <Typography variant="body2">Validation</Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '20px', height: '3px', backgroundColor: '#10b981' }}></div>
            <Typography variant="body2">Completion</Typography>
          </div>
        </>
      );
    }
  };

  return (
    <>
      <div className="group-info-test" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}> 
        <div style={{display:"flex", justifyContent:"space-between", alignItems: 'center', flexShrink: 0}}>
          <Typography variant="h6" fontWeight={'light'}>
            AI Interaction Over Time
          </Typography>
          <Typography variant="h6" fontWeight={'light'}>
            Avg Questions/Student: {questionCategories.Avg_question_per_student}
          </Typography>
        </div>

        {/* Chart Section */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: chartView === 'questions' ? 'bold' : 'normal',
                    cursor: 'pointer'
                  }}
                  onClick={() => setChartView('questions')}
                >
                  Questions
                </Typography>
                <Typography variant="body2" style={{ color: '#bdbdbd' }}>|</Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: chartView === 'intent' ? 'bold' : 'normal',
                    cursor: 'pointer'
                  }}
                  onClick={() => setChartView('intent')}
                >
                  Intent
                </Typography>
              </div>
            </div>

            {interactions.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: 300,
                border: '1px dashed #ccc',
                borderRadius: 2,
                backgroundColor: '#f9f9f9'
              }}>
                <Typography variant="body1" color="text.secondary">
                  📊 AI interaction data will appear here as students ask questions
                </Typography>
              </Box>
            ) : (
              renderChart()
            )}
            
            <Typography variant="caption" style={{ display: 'block', textAlign: 'center', marginTop: '10px', color: '#666' }}>
              Click on any data point on the chart to view interaction details for that time period
            </Typography>
          </div>

          {/* Dynamic Legend */}
          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '15px',
            justifyContent: 'center'
          }}>
            {renderLegend()}
          </div>

          {/* Playback Controls */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            padding: '12px 16px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            marginTop: '16px'
          }}>
            <IconButton onClick={handlePlayPause} color="primary" size="small">
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <IconButton onClick={handleReset} size="small">
              <RestartAltIcon />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: '50px' }}>
              {formatTime(currentTime)}
            </Typography>
            <Slider
              value={currentTime}
              onChange={handleSliderChange}
              min={0}
              max={maxTime}
              step={1/60}
              sx={{ flex: 1 }}
            />
            <Typography variant="body2" sx={{ minWidth: '50px' }}>
              {formatTime(maxTime)}
            </Typography>
          </Box>
        </div>
      </div>

      {/* Interaction Details Modal */}
      <InteractionDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        interactions={selectedInteractions}
        timeRange={selectedTimeRange}
      />
    </>
  );
}  

export default InteractionList;